import { useEffect } from 'react';

interface HreflangAlternate {
  hreflang: string;
  href: string;
}

interface DocumentMetaOptions {
  title: string;
  description?: string;
  canonical?: string;
  noindex?: boolean;
  ogImage?: string;
  hreflangAlternates?: HreflangAlternate[];
  /** Arbitrary JSON-LD objects, injected as <script type="application/ld+json"> tags. */
  structuredData?: object[];
}

function upsertMeta(selector: string, create: () => HTMLMetaElement, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = create();
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function removeMeta(selector: string) {
  document.head.querySelector(selector)?.remove();
}

/**
 * Sets the document title and description/OG/Twitter/canonical/robots/hreflang
 * meta tags, plus JSON-LD structured data, for the current route. This is a CSR
 * SPA with no SSR, so index.html's static tags stay as the fallback Lighthouse/
 * crawlers see before hydration; this hook only updates them once the route's
 * real content is known. Everything it sets is cleaned up on unmount so one
 * page's meta never leaks into the next route.
 */
export function useDocumentMeta({
  title,
  description,
  canonical,
  noindex = false,
  ogImage,
  hreflangAlternates,
  structuredData,
}: DocumentMetaOptions) {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = title;

    if (description) {
      upsertMeta(
        'meta[name="description"]',
        () => {
          const el = document.createElement('meta');
          el.setAttribute('name', 'description');
          return el;
        },
        description,
      );
      upsertMeta(
        'meta[property="og:description"]',
        () => {
          const el = document.createElement('meta');
          el.setAttribute('property', 'og:description');
          return el;
        },
        description,
      );
      upsertMeta(
        'meta[name="twitter:description"]',
        () => {
          const el = document.createElement('meta');
          el.setAttribute('name', 'twitter:description');
          return el;
        },
        description,
      );
    }

    upsertMeta(
      'meta[property="og:title"]',
      () => {
        const el = document.createElement('meta');
        el.setAttribute('property', 'og:title');
        return el;
      },
      title,
    );
    upsertMeta(
      'meta[name="twitter:title"]',
      () => {
        const el = document.createElement('meta');
        el.setAttribute('name', 'twitter:title');
        return el;
      },
      title,
    );

    if (ogImage) {
      upsertMeta(
        'meta[property="og:image"]',
        () => {
          const el = document.createElement('meta');
          el.setAttribute('property', 'og:image');
          return el;
        },
        ogImage,
      );
      upsertMeta(
        'meta[name="twitter:image"]',
        () => {
          const el = document.createElement('meta');
          el.setAttribute('name', 'twitter:image');
          return el;
        },
        ogImage,
      );
    }

    let canonicalLink: HTMLLinkElement | null = null;
    if (canonical) {
      canonicalLink = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
      if (!canonicalLink) {
        canonicalLink = document.createElement('link');
        canonicalLink.setAttribute('rel', 'canonical');
        document.head.appendChild(canonicalLink);
      }
      canonicalLink.setAttribute('href', canonical);
    }

    let robotsMeta: HTMLMetaElement | null = null;
    if (noindex) {
      robotsMeta = document.head.querySelector<HTMLMetaElement>('meta[name="robots"]');
      if (!robotsMeta) {
        robotsMeta = document.createElement('meta');
        robotsMeta.setAttribute('name', 'robots');
        document.head.appendChild(robotsMeta);
      }
      robotsMeta.setAttribute('content', 'noindex,follow');
    }

    const hreflangLinks: HTMLLinkElement[] = [];
    let removedStaticHreflangLinks: HTMLLinkElement[] = [];
    if (hreflangAlternates) {
      // Remove any static hreflang links (e.g. index.html's) so they don't
      // duplicate these — restored on cleanup.
      removedStaticHreflangLinks = Array.from(
        document.head.querySelectorAll<HTMLLinkElement>('link[rel="alternate"][hreflang]'),
      );
      removedStaticHreflangLinks.forEach((el) => el.remove());

      for (const alt of hreflangAlternates) {
        const link = document.createElement('link');
        link.setAttribute('rel', 'alternate');
        link.setAttribute('hreflang', alt.hreflang);
        link.setAttribute('href', alt.href);
        document.head.appendChild(link);
        hreflangLinks.push(link);
      }
    }

    const structuredDataScripts: HTMLScriptElement[] = [];
    // index.html carries the homepage's own ItemList/FAQPage (data-home-only).
    // A page with its own structured data must not also claim those — detach
    // them while it's mounted, put them back on the way out.
    const detachedHomeScripts: HTMLScriptElement[] = structuredData
      ? Array.from(document.head.querySelectorAll<HTMLScriptElement>('script[data-home-only]'))
      : [];
    detachedHomeScripts.forEach((el) => el.remove());
    if (structuredData) {
      for (const data of structuredData) {
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.textContent = JSON.stringify(data);
        document.head.appendChild(script);
        structuredDataScripts.push(script);
      }
    }

    return () => {
      document.title = previousTitle;
      removeMeta('meta[name="description"]');
      removeMeta('meta[property="og:description"]');
      removeMeta('meta[name="twitter:description"]');
      removeMeta('meta[property="og:title"]');
      removeMeta('meta[name="twitter:title"]');
      if (ogImage) {
        removeMeta('meta[property="og:image"]');
        removeMeta('meta[name="twitter:image"]');
      }
      canonicalLink?.remove();
      robotsMeta?.remove();
      hreflangLinks.forEach((el) => el.remove());
      removedStaticHreflangLinks.forEach((el) => document.head.appendChild(el));
      structuredDataScripts.forEach((el) => el.remove());
      detachedHomeScripts.forEach((el) => document.head.appendChild(el));
    };
  }, [title, description, canonical, noindex, ogImage, hreflangAlternates, structuredData]);
}
